/**
 * Assign Guard Page - Microsoft 365 Style
 * Manage guard assignments to sites and shifts
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  SearchRegular,
  PersonRegular,
  BuildingRegular,
  ShieldTaskRegular,
  EyeRegular,
  ArrowSyncRegular,
  AddRegular,
  PersonDeleteRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown, TablePagination } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  dashboardCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  filterSection: {
    marginBottom: '24px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr) auto',
    gap: '12px',
    alignItems: 'end',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #e1dfdd',
  },
  detailLabel: {
    fontWeight: '600',
    color: '#605e5c',
  },
  employeePhotoPlaceholder: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    backgroundColor: '#f3f2f1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    border: '2px solid #e1dfdd',
    fontSize: '48px',
    color: '#605e5c',
  },
});

interface GuardAssignment {
  id: number;
  client_id: number;
  site_id: number;
  shift_id: number;
  employee_id: number;
  status: string;
  created_at: string;
  client_name: string;
  zone_name: string;
  site_name: string;
  shift_name: string;
  start_time: string;
  end_time: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface FilterOptions {
  zones: Array<{ zoneid: number; name: string }>;
  clients: Array<{ id: number; clientid: string; client_name: string }>;
  sites: Array<{ id: number; name: string; client_id: number }>;
  shifts: Array<{ id: number; name: string }>;
}

export const AssignGuardFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<GuardAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<GuardAssignment[]>([]);
  const { paginatedData, currentPage, totalItems, itemsPerPage, setCurrentPage, setItemsPerPage } =
    usePagination<GuardAssignment>({
      data: filteredAssignments,
      initialItemsPerPage: 10,
    });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState<GuardAssignment | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [assignmentToUnassign, setAssignmentToUnassign] = useState<GuardAssignment | null>(null);
  const [unassigning, setUnassigning] = useState(false);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    zones: [],
    clients: [],
    sites: [],
    shifts: [],
  });

  const [filters, setFilters] = useState({
    zone: '',
    client: '',
    site: '',
    shift: '',
  });

  const [stats, setStats] = useState({
    totalClients: 0,
    totalSites: 0,
    guardsOnDuty: 0,
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchAssignments();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, assignments]);

  const fetchFilterOptions = async () => {
    try {
      const [zonesRes, clientsRes, sitesRes, shiftsRes] = await Promise.all([
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-clients'),
        ApiClient.get('/get-sites'),
        ApiClient.get('/get-shifts'),
      ]);

      setFilterOptions({
        zones: zonesRes.data.data || [],
        clients: clientsRes.data.data || [],
        sites: sitesRes.data.data || [],
        shifts: shiftsRes.data.data || [],
      });
    } catch (error: unknown) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-assign-guards');
      if (response.data.status === 'success') {
        const rawData = response.data.data || [];
        // Deduplicate assignments by id to handle backend JOIN duplicates
        const data = rawData.filter(
          (assignment: GuardAssignment, index: number, self: GuardAssignment[]) =>
            index === self.findIndex(a => a.id === assignment.id)
        );
        setAssignments(data);

        const uniqueClients = new Set(data.map((a: GuardAssignment) => a.client_id));
        const uniqueSites = new Set(data.map((a: GuardAssignment) => a.site_id));
        const activeGuards = data.filter((a: GuardAssignment) => a.status === 'active').length;

        setStats({
          totalClients: uniqueClients.size,
          totalSites: uniqueSites.size,
          guardsOnDuty: activeGuards,
        });
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...assignments];

    if (searchQuery) {
      filtered = filtered.filter(
        a =>
          a.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.site_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.zone_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          `${a.first_name} ${a.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredAssignments(filtered);
  };

  const handleSearch = () => {
    let filtered = [...assignments];

    if (filters.zone) {
      filtered = filtered.filter(a => a.zone_name === filters.zone);
    }
    if (filters.client) {
      filtered = filtered.filter(a => a.client_name === filters.client);
    }
    if (filters.site) {
      filtered = filtered.filter(a => a.site_name === filters.site);
    }
    if (filters.shift) {
      filtered = filtered.filter(a => a.shift_name === filters.shift);
    }

    setFilteredAssignments(filtered);
  };

  const handleViewAssignment = (assignment: GuardAssignment) => {
    setSelectedAssignment(assignment);
    setViewDialogOpen(true);
  };

  const handleUnassign = async () => {
    if (!assignmentToUnassign) {
      return;
    }

    setUnassigning(true);
    try {
      const response = await ApiClient.post('/delete-assign-guard', {
        id: assignmentToUnassign.id,
      });
      if (response.data.status === 'success') {
        newToast('success', 'Guard unassigned successfully');
        setUnassignDialogOpen(false);
        setAssignmentToUnassign(null);
        fetchAssignments();
      } else {
        newToast('error', response.data.message || 'Failed to unassign guard');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setUnassigning(false);
  };

  const getFilteredSites = () => {
    console.log('=== getFilteredSites called ===');
    console.log('filters.client:', filters.client);
    console.log('filterOptions.clients:', filterOptions.clients);
    console.log('filterOptions.sites:', filterOptions.sites);

    if (!filters.client) {
      console.log('No client selected, returning empty array');
      return [];
    }

    const selectedClient = filterOptions.clients.find(c => c.client_name === filters.client);
    console.log('selectedClient found:', selectedClient);

    if (!selectedClient) {
      console.log('Client not found in filterOptions.clients');
      return [];
    }

    const filtered = filterOptions.sites.filter(s => s.client_id === selectedClient.id);
    console.log(`Filtered sites (client_id === ${selectedClient.id}):`, filtered);
    console.log('Number of filtered sites:', filtered.length);

    return filtered;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Assign Guard</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${assignments.length} total assignments`}
        </Text>
      </div>

      {/* Action Button Card */}
      <ContentCard>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={() => navigate('/account/guard-operations/assign-guard-form')}
        >
          Assign New Guard
        </Button>
      </ContentCard>

      {/* Dashboard Cards */}
      <div className={styles.dashboardCards}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}>
            <BuildingRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.totalClients}</Text>
            <Text className={styles.statLabel}>Total Active Clients</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
            <ShieldTaskRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.totalSites}</Text>
            <Text className={styles.statLabel}>Total Active Sites</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e8f5e9', color: '#388e3c' }}>
            <PersonRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.guardsOnDuty}</Text>
            <Text className={styles.statLabel}>Total Guards on Duty</Text>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <ContentCard>
        <div className={styles.filterSection}>
          <Text
            style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'block' }}
          >
            Filter Assignments
          </Text>
          <div className={styles.filterGrid}>
            <SearchableDropdown
              placeholder="Search zones..."
              options={filterOptions.zones.map(z => ({ id: z.name, label: z.name }))}
              selectedValue={filters.zone}
              onSelect={value => setFilters({ ...filters, zone: value })}
            />

            <SearchableDropdown
              placeholder="Search clients..."
              options={filterOptions.clients.map(c => ({
                id: c.client_name,
                label: c.client_name,
              }))}
              selectedValue={filters.client}
              onSelect={value => setFilters({ ...filters, client: value, site: '' })}
            />

            <SearchableDropdown
              key={filters.client || 'no-client'}
              placeholder="Search sites..."
              options={getFilteredSites().map(s => ({ id: s.name, label: s.name }))}
              selectedValue={filters.site}
              onSelect={value => setFilters({ ...filters, site: value })}
              disabled={!filters.client}
            />

            <SearchableDropdown
              placeholder="Search shifts..."
              options={filterOptions.shifts.map(s => ({ id: s.name, label: s.name }))}
              selectedValue={filters.shift}
              onSelect={value => setFilters({ ...filters, shift: value })}
            />

            <Button appearance="primary" onClick={handleSearch}>
              Search
            </Button>
          </div>
        </div>
      </ContentCard>

      {/* Search Bar */}
      <div className={styles.searchBar} style={{ marginTop: '24px' }}>
        <Input
          placeholder="Search by client, site, zone, or guard name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Assignments Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading assignments...</Text>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No assignments found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery || Object.values(filters).some(f => f)
                ? 'Try adjusting your search or filters'
                : 'No guard assignments available'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Client Name</th>
                <th className={styles.th}>Zone</th>
                <th className={styles.th}>Client Site</th>
                <th className={styles.th}>Guard Name</th>
                <th className={styles.th}>Assigned Shift</th>
                <th className={styles.th}>Date Assigned</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(assignment => (
                <tr key={assignment.id} className={styles.tr}>
                  <td className={styles.td}>{assignment.client_name}</td>
                  <td className={styles.td}>
                    <Badge appearance="outline" color="informative">
                      {assignment.zone_name}
                    </Badge>
                  </td>
                  <td className={styles.td}>{assignment.site_name}</td>
                  <td className={styles.td}>
                    {assignment.first_name} {assignment.last_name}
                  </td>
                  <td className={styles.td}>
                    <Badge appearance="filled" color="brand">
                      {assignment.shift_name}
                    </Badge>
                  </td>
                  <td className={styles.td}>{formatDate(assignment.created_at)}</td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EyeRegular />}
                        onClick={() => handleViewAssignment(assignment)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<ArrowSyncRegular />}
                        onClick={() =>
                          navigate('/account/guard-operations/guard-shift-reassign', {
                            state: {
                              id: assignment.id,
                              employee_id: assignment.employee_id,
                              first_name: assignment.first_name,
                              last_name: assignment.last_name,
                              email: assignment.email,
                              phone: assignment.phone,
                              current_client_id: assignment.client_id.toString(),
                              current_site_id: assignment.site_id.toString(),
                              current_shift_id: assignment.shift_id.toString(),
                              start_date: new Date().toISOString().split('T')[0],
                            },
                          })
                        }
                      >
                        Re-assign
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<PersonDeleteRegular />}
                        onClick={() => {
                          setAssignmentToUnassign(assignment);
                          setUnassignDialogOpen(true);
                        }}
                      >
                        Unassign
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredAssignments.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* View Assignment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={(e, data) => setViewDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '500px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Assignment Details</DialogTitle>
            <DialogContent>
              {selectedAssignment && (
                <div>
                  {/* Employee Photo Placeholder */}
                  <div className={styles.employeePhotoPlaceholder}>
                    <PersonRegular />
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Client:</span>
                    <span>{selectedAssignment.client_name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Zone:</span>
                    <span>{selectedAssignment.zone_name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Site:</span>
                    <span>{selectedAssignment.site_name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Guard:</span>
                    <span>
                      {selectedAssignment.first_name} {selectedAssignment.last_name}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Email:</span>
                    <span>{selectedAssignment.email}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Phone:</span>
                    <span>{selectedAssignment.phone}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Shift:</span>
                    <span>{selectedAssignment.shift_name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Shift Time:</span>
                    <span>
                      {selectedAssignment.start_time} - {selectedAssignment.end_time}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Status:</span>
                    <Badge
                      appearance="filled"
                      color={selectedAssignment.status === 'active' ? 'success' : 'warning'}
                    >
                      {selectedAssignment.status}
                    </Badge>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Date Assigned:</span>
                    <span>{formatDate(selectedAssignment.created_at)}</span>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Unassign Confirmation Dialog */}
      <Dialog
        open={unassignDialogOpen}
        onOpenChange={(_, data) => setUnassignDialogOpen(data.open)}
      >
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Unassign Guard</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to unassign{' '}
                <strong>
                  {assignmentToUnassign?.first_name} {assignmentToUnassign?.last_name}
                </strong>{' '}
                from <strong>{assignmentToUnassign?.site_name}</strong>?
              </Text>
              <Text
                style={{ display: 'block', marginTop: '12px', color: '#605e5c', fontSize: '13px' }}
              >
                This will set the assignment status to inactive.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setUnassignDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleUnassign} disabled={unassigning}>
                {unassigning ? 'Unassigning...' : 'Unassign'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default AssignGuardFluentUI;
